/**
 * แดชบอร์ดสรุป
 * ตัวเลขทุกตัวคำนวณฝั่ง server ด้วย aggregate query ไม่ดึงแถวมานับที่ browser
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, type DashboardSummary } from '../lib/api';
import { CAPTURE_STATUS_LABELS_TH, CAPTURE_STATUSES } from '../../shared/types';

export function DashboardRoute() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .dashboard(controller.signal)
      .then(setSummary)
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(caught instanceof ApiError ? caught.message : 'โหลดข้อมูลสรุปไม่สำเร็จ');
      });
    return () => controller.abort();
  }, []);

  if (error) {
    return (
      <div className="wrap-wide">
        <div className="banner banner-bad" role="alert">
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="wrap-wide">
        <div className="empty">
          <span className="spinner" style={{ margin: '0 auto' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="wrap-wide stack-lg">
      <h1>แดชบอร์ด</h1>

      <div className="tiles">
        <Tile value={summary.total} label="รายการทั้งหมด" />
        <Tile value={summary.todayCompleted} label="สำเร็จวันนี้" />
        <Tile value={summary.last7DaysCompleted} label="สำเร็จ 7 วันล่าสุด" />
        <Tile
          value={summary.gpsOverrideRequested}
          label="รออนุมัติพิกัด"
          tone={summary.gpsOverrideRequested > 0 ? 'warn' : undefined}
        />
        <Tile value={summary.gpsOverrideApproved} label="อนุมัติพิกัดแล้ว" />
        <Tile
          value={summary.duplicateRegistrations}
          label="เลขทะเบียนที่ซ้ำกัน"
          tone={summary.duplicateRegistrations > 0 ? 'warn' : undefined}
        />
      </div>

      {summary.gpsOverrideRequested > 0 && (
        <div className="banner banner-warn">
          <div className="grow">
            <b>มี {summary.gpsOverrideRequested} รายการรออนุมัติพิกัด</b>
            เจ้าหน้าที่ไม่สามารถปิดรายการได้จนกว่าจะมีการอนุมัติ
          </div>
          <Link className="btn btn-secondary" to="/search">
            ดูรายการ
          </Link>
        </div>
      )}

      <section className="stack">
        <h2>แยกตามสถานะ</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">สถานะ</th>
                <th scope="col">จำนวน</th>
                <th scope="col">สัดส่วน</th>
              </tr>
            </thead>
            <tbody>
              {CAPTURE_STATUSES.map((status) => {
                const count = summary.byStatus[status] ?? 0;
                const share = summary.total > 0 ? (count / summary.total) * 100 : 0;
                return (
                  <tr key={status}>
                    <th scope="row">{CAPTURE_STATUS_LABELS_TH[status]}</th>
                    <td className="mono">{count.toLocaleString('th-TH')}</td>
                    <td className="mono">{share.toFixed(1)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Tile({ value, label, tone }: { value: number; label: string; tone?: 'warn' }) {
  return (
    <div className="tile">
      <div className="tile-value" style={tone === 'warn' ? { color: 'var(--warn)' } : undefined}>
        {value.toLocaleString('th-TH')}
      </div>
      <div className="tile-label">{label}</div>
    </div>
  );
}
